import * as React from "react";
import { connect } from "common/connect";
import StoreKeys from "common/storeKeys";
import styled from "styled-components";
import { Colors } from "styles/Colors";
import Store from "common/store";
// eslint-disable-next-line no-unused-vars
import { EventFormatData } from "common/helpers/eventFormatHelper";
import { EventStageTypes } from "common/constants/EventStageTypes";
import ModalHelper from "common/helpers/modalHelper";
import SurveyForPicksModal from "common/components/Modals/SurveyForPicksModal/SurveyForPicksModal";

const propKeys = {
	eventData: StoreKeys.EVENT.EVENTDATA,
	format: StoreKeys.EVENT.FORMAT,
	stageIndex: StoreKeys.EVENT.STAGE_INDEX,
	substageIndex: StoreKeys.EVENT.SUBSTAGE_INDEX,
	view: StoreKeys.EVENT.VIEW,
};

/**
 * @typedef StageSelectorCProps
 * @type {object}
 * @property {any} eventData
 * @property {EventFormatData} format
 * @property {number} stageIndex
 * @property {number} substageIndex
 */

const StageLabel = styled.label`
	margin: auto;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	line-height: 1.2;
	// two lines tall always, so a wrapping name doesn't make its chip taller than the rest
	min-height: 2.4em;
`;

const StageButton = styled.div`
	display: flex;
	width: 100px;
	margin: 4px;
	padding: 8px 12px;
	border-radius: 12px;
	text-align: center;
	background: ${({ selected }) => (selected ? Colors.primary : Colors.panel)};
	color: ${({ selected, unlocked }) =>
		selected
			? Colors.panel
			: unlocked
			? Colors.primary
			: Colors.panelLighterColor};
	cursor: ${({ unlocked }) => (unlocked ? "pointer" : "default")};
	font-weight: 400;
	overflow-wrap: break-word;
	align-content: center;
	justify-content: center;
	transition: background 120ms ease;
	// the browser's tap flash ignores border-radius, so we paint our own
	-webkit-tap-highlight-color: transparent;

	&:hover {
		background: ${({ selected, unlocked }) =>
			selected || !unlocked ? undefined : Colors.panelLight};
	}

	${StageLabel} {
		cursor: ${({ unlocked }) => (unlocked ? "pointer" : "default")};
	}
`;

/** Breaks only before words over 3 chars, so "Round of 32" keeps "of 32" on one line. */
function stickyShortWords(text) {
	return (text ?? "")
		.split(" ")
		.reduce(
			(acc, word, i) =>
				i === 0 ? word : acc + (word.length > 3 ? " " : "\u00A0") + word,
			""
		);
}

/**
 * @param {StageSelectorCProps} props
 */
function StageSelectorC({
	eventData,
	format,
	stageIndex,
	substageIndex,
	view,
	disableOverview,
}) {
	// For RL 2024 Finals, force into the swiss stage.
	if (eventData.id === 104) {
		stageIndex = 0;
	}

	const currentStage = format.stages[stageIndex];
	/** @type {EventStage} */
	const currentEventStage = eventData.eventStages[stageIndex];

	const championEnabled = !disableOverview && !!format.championPick?.enabled;
	const isOverview = championEnabled && view === "overview";

	// Single-stage events still need the selector when the Overview chip exists.
	if (format.stages.length <= 1 && !championEnabled) {
		return <></>;
	}

	const changeStage = (i) => {
		if (isOverview) {
			Store.set(StoreKeys.EVENT.VIEW, "picks");
		}

		if (i === stageIndex) {
			return;
		}

		// If the playoffs are selected, prompt the survey will be used modal.
		if (eventData.id === 104 && i === 1) {
			ModalHelper.openModal(<SurveyForPicksModal />)
		}

		if (i > eventData.stage) {
			return;
		}

		const updates = {};
		updates[StoreKeys.EVENT.STAGE_INDEX] = i;
		updates[StoreKeys.EVENT.SUBSTAGE_INDEX] = 0;
		updates[StoreKeys.EVENT.IS_EDITING] = false;
		updates[StoreKeys.EVENT.STEP] = 0;

		Store.setMany(updates);
	};

	const changeSubstage = (i) => {
		if (i === substageIndex) {
			return;
		}

		if (i >= currentEventStage.eventSubstage.length) {
			return;
		}

		Store.set(StoreKeys.EVENT.SUBSTAGE_INDEX, i);
	};

	const isGroupStageBrackets =
		currentStage?.type === EventStageTypes.Group &&
		currentStage?.groupStageDetails?.hasGroupBrackets;

	return (
		<>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					marginTop: championEnabled ? 8 : 0,
					marginBottom: championEnabled ? 6 : 8,
				}}
			>
				{championEnabled && (
					<StageButton
						selected={isOverview}
						unlocked={true}
						onClick={() => Store.set(StoreKeys.EVENT.VIEW, "overview")}
						key="overview"
					>
						<StageLabel>Overview</StageLabel>
					</StageButton>
				)}
				{format.stages.map((s, i) => {
					const selected = i === stageIndex && !isOverview;
					const unlocked = i <= eventData.stage || (eventData.id === 104 && i === 1);
					return (
						<StageButton
							selected={selected}
							unlocked={unlocked}
							onClick={() => unlocked && changeStage(i)}
							key={i}
						>
							<StageLabel>{stickyShortWords(s.name)}</StageLabel>
						</StageButton>
					);
				})}
			</div>

			{!isOverview && isGroupStageBrackets && currentEventStage?.eventSubstage?.length > 1 && (
				<div
					style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}
				>
					{currentEventStage.eventSubstage.map((ss, i) => {
						const selected = i === substageIndex;
						return (
							<StageButton
								selected={selected}
								unlocked={true}
								onClick={() => changeSubstage(i)}
								key={i}
							>
								<StageLabel>{stickyShortWords(ss.title)}</StageLabel>
							</StageButton>
						);
					})}
				</div>
			)}
		</>
	);
}

export default function StageSelector(props) {
	return connect(<StageSelectorC />, propKeys, props);
}
